import React from 'react';
import Link from 'next/link';
import type { Lesson, Level } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { ArrowRight, BookOpen } from 'lucide-react';

interface Props {
  lesson?: Lesson;
  level: Level;
  language: Language;
}

export default function NextLessonCard({ lesson, level, language }: Props) {
  const lessonHref = lesson ? `/lesson-screen?lessonId=${lesson.id}` : '/lesson-screen';

  return (
    <div
      className="bg-card rounded-2xl border border-border card-shadow p-5 flex flex-col justify-between h-full"
      style={{ minHeight: '220px' }}
    >
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{level.icon}</span>
          <div>
            <p className="text-xs text-muted-foreground font-500">
              {language === 'fr' ? `Niveau ${level.number}` : `Уровень ${level.number}`}
            </p>
            <p className="text-sm font-600 text-foreground">{level.title[language]}</p>
          </div>
        </div>

        {lesson ? (
          <>
            <h3 className="text-base font-700 text-foreground mb-2">{lesson.title[language]}</h3>
            <p className="text-sm text-muted-foreground">{lesson.description[language]}</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-600">
                +{lesson.xpReward} XP
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-600 ${
                  lesson.status === 'in_progress' ?'bg-amber-100 text-amber-700' :'bg-green-100 text-green-700'
                }`}
              >
                {lesson.status === 'in_progress' ? t(language,'learning_in_progress')
                  : t(language, 'learning_available')}
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? 'Niveau complété! Passez au suivant.' : 'Уровень завершен! Переходите к следующему.'}
          </p>
        )}
      </div>

      <Link
        href={lessonHref}
        className="mt-4 flex items-center justify-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl text-sm font-600 hover:bg-primary/90 transition-all duration-150 active:scale-95"
      >
        <BookOpen size={15} />
        {lesson?.status === 'in_progress' ? t(language,'learning_continue')
          : t(language, 'learning_start')}
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}